/**
 * TanStack Query hooks for subscriptions
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionsApi } from '@/services/api';
import { queryKeys } from '@/config/queryClient';
import { useIsAuthenticated } from '@/store/authStore';
import type { Language } from '@/types';

/**
 * Hook to fetch pricing plans
 */
export function usePricingPlans() {
  return useQuery({
    queryKey: queryKeys.subscriptions.plans(),
    queryFn: () => subscriptionsApi.getPricingPlans(),
    staleTime: 10 * 60 * 1000, // Plans don't change often
  });
}

/**
 * Hook to fetch subscription status (signed-in users only)
 */
export function useSubscriptionStatus() {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: queryKeys.subscriptions.status(),
    queryFn: () => subscriptionsApi.getSubscriptionStatus(),
    enabled: isAuthenticated,
  });
}

/**
 * Hook to fetch subscription features
 */
export function useSubscriptionFeatures() {
  return useQuery({
    queryKey: queryKeys.subscriptions.features(),
    queryFn: () => subscriptionsApi.getFeatures(),
  });
}

/**
 * Hook to create a checkout session
 */
export function useCreateCheckout() {
  return useMutation({
    mutationFn: ({ planId, language }: { planId: string; language: Language }) =>
      subscriptionsApi.createCheckout(planId, language),
    onSuccess: (data: { checkout_url?: string }) => {
      // Redirect to Stripe checkout if URL is provided
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    },
  });
}

/**
 * Hook to cancel subscription
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => subscriptionsApi.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.status() });
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.features() });
    },
  });
}

/**
 * Hook to create portal session
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: () => subscriptionsApi.createPortalSession(),
    onSuccess: (data: { url?: string }) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
  });
}

/**
 * Hook to verify checkout session after successful payment
 */
export function useVerifySession() {
  const queryClient = useQueryClient();

  return useMutation({
    // The plan comes from the paid Stripe price; the client never sends one.
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      subscriptionsApi.verifySession(sessionId),
    onSuccess: () => {
      // Invalidate all subscription-related queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.status() });
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.features() });
    },
  });
}
